import { notify } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useDirtyRoute } from '../../../../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { useForm } from '../../../../../../../hooks'
import { usePredefinedInvoiceSettingsFormValues } from '../hooks/usePredefinedInvoiceSettingsFormValues'
import { useUpdateAutomaticPaymentReminders } from '../hooks/useUpdateAutomaticPaymentReminders'
import { useUpdateInvoiceSettings } from '../hooks/useUpdateInvoiceSettings'
import { useUpdateInvoiceTemplateSettings } from '../hooks/useUpdateInvoiceTemplateSettings'
import { InvoiceSettingsForm } from '../types/invoiceSettingsForm'
import { InvoiceSettingsFormSchema } from '../types/invoiceSettingsFormSchema'
import { getDefaultValues } from '../utils/getDefaultValues'
import { getInvoiceSettingsFormSchema } from '../utils/getInvoiceSettingsFormSchema'
import { getNotifySaveError } from '../utils/getNotifySaveError'
import { prepareInvoiceSettingsPayloadTexts } from '../utils/prepareInvoiceSettingsPayloadTexts'

interface ContextState {
  isLoading: boolean
  isSubmitting: boolean
  submitForm: () => void
}

const InvoiceSettingsFormContext = createContext<ContextState | undefined>(undefined)

interface InvoiceSettingsFormContextProviderProps {
  children?: ReactNode
}

export const InvoiceSettingsFormContextProvider = ({
  children,
}: InvoiceSettingsFormContextProviderProps): ReactElement => {
  const { t } = useTranslation()
  const { setDirty } = useDirtyRoute()

  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { isLoading, predefinedInvoiceSettingsFormValues } = usePredefinedInvoiceSettingsFormValues()
  const { update: updateInvoiceSettings, isProcessing: isProcessingInvoiceSettings } = useUpdateInvoiceSettings()
  const { update: updateInvoiceTemplateSettings, isProcessing: isProcessingInvoiceTemplateSettings } =
    useUpdateInvoiceTemplateSettings()
  const {
    update: updateAutomaticPaymentRemindersSettings,
    isProcessing: isProcessingAutomaticPaymentRemindersSettings,
  } = useUpdateAutomaticPaymentReminders()
  const { organization } = useUserOrganization()
  const organizationName = organization?.name

  const {
    Form,
    formState: { isDirty, dirtyFields },
    handleSubmit,
    reset: resetForm,
  } = useForm<InvoiceSettingsFormSchema>({
    defaultValues: getDefaultValues(t, { organizationName, isFrenchComplianceMode }),
    validationSchema: getInvoiceSettingsFormSchema(t, isFrenchComplianceMode),
  })

  const isSubmitting =
    isProcessingInvoiceSettings || isProcessingInvoiceTemplateSettings || isProcessingAutomaticPaymentRemindersSettings

  const notifySaveError = useMemo(() => getNotifySaveError(t), [t])

  const getInvoiceSettingsUpdateRequests = useCallback(
    (formData: InvoiceSettingsForm) => {
      const formUpdateRequests = [updateInvoiceSettings(formData), updateInvoiceTemplateSettings(formData)]
      if (dirtyFields.automaticPaymentRemindersSettings) {
        formUpdateRequests.push(updateAutomaticPaymentRemindersSettings(formData))
      }

      return formUpdateRequests
    },
    // dirtyFields is intentionally excluded to avoid unnecessary callback recreation
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateAutomaticPaymentRemindersSettings, updateInvoiceSettings, updateInvoiceTemplateSettings],
  )

  const handleInvoiceSettingsFormSubmit = useCallback(
    async (formData: InvoiceSettingsForm) => {
      try {
        const formDataToSubmit = {
          ...formData,
          ...prepareInvoiceSettingsPayloadTexts(formData, t),
        }
        await Promise.all(getInvoiceSettingsUpdateRequests(formDataToSubmit))
        setDirty(false)
        notify({
          id: NotificationKeys.ExternalInvoiceSettingsUpdate,
          message: t('settings.organization.external_invoice.save.success'),
          variant: 'success',
        })
      } catch {
        notifySaveError()
      }
    },
    [getInvoiceSettingsUpdateRequests, notifySaveError, setDirty, t],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handleInvoiceSettingsFormSubmit, notifySaveError)()
  }, [handleSubmit, handleInvoiceSettingsFormSubmit, notifySaveError])

  useEffect(() => {
    if (predefinedInvoiceSettingsFormValues && !isLoading && !isDirty) {
      const updatedFormValues = getDefaultValues(t, {
        predefinedValues: predefinedInvoiceSettingsFormValues,
        organizationName,
        isFrenchComplianceMode,
      })

      resetForm(updatedFormValues)
    }
  }, [isDirty, isLoading, isFrenchComplianceMode, organizationName, predefinedInvoiceSettingsFormValues, resetForm, t])

  useEffect(() => {
    setDirty(isDirty)
  }, [isDirty, setDirty])

  const contextValue = useMemo(() => ({ isLoading, isSubmitting, submitForm }), [isLoading, isSubmitting, submitForm])

  return (
    <InvoiceSettingsFormContext.Provider value={contextValue}>
      <Form>{children}</Form>
    </InvoiceSettingsFormContext.Provider>
  )
}

export const useInvoiceSettingsForm = () => {
  const context = useContext(InvoiceSettingsFormContext)

  if (!context) {
    throw new Error('InvoiceSettingsFormContextProvider is missing in the module!')
  }

  return context
}
